/**
 * ALBAU-922: renders the print file for a masked photo from the customer's original upload.
 *
 * This replaces the editor-canvas crop (`cropMaskedImageWithPadding`) for masked photos. The editor
 * canvas is 1250 px wide and the photo is already resampled on it, so cropping that canvas throws away
 * resolution the original still has. Here the original is drawn again, with the same framing the
 * editor used, straight into a canvas the size of the print window (capped at 1200 px on the long
 * edge), clipped to the mask and set on white.
 *
 * It rejects on any failure and never returns the original. The caller (`updateSelections`) falls back
 * to the old editor-canvas crop when it rejects.
 *
 * Crops run one at a time, oldest first. Only one decoded original is held in memory at once (iOS
 * Safari runs out of canvas memory quickly), and when two saves to the same slot are in flight the
 * newest one still lands last.
 *
 * The result is remembered per print source object. GROUPED steps copy one print source to several
 * option groups (and crop each image line twice), so the same object arrives many times; each call
 * after the first reuses the first print file instead of decoding and encoding the original again
 * (about a second per 12 MP photo on a phone). A new Save always makes a new print source object, so
 * it is cropped afresh and the newest-wins ordering is unchanged. A failed crop is forgotten, so it
 * can be retried.
 */
import {
  computePrintCropGeometry,
  isPrintSource,
  maskRectOnOutput,
  PRINT_CROP_JPEG_QUALITY,
  printCropMatrix,
  type PrintSource
} from './printCropGeometry';

/** Decodes with `new Image()` (not `createImageBitmap`) so EXIF orientation matches the editor's decode. */
function loadImage(url: string, failure: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(failure));
    image.src = url;
  });
}

async function renderPrintCrop(printSource: PrintSource): Promise<File> {
  if (!isPrintSource(printSource)) throw new Error('Invalid print source');
  const { file, maskBlob, transform } = printSource;

  const originalUrl = URL.createObjectURL(file);
  const maskUrl = URL.createObjectURL(maskBlob);
  let canvas: HTMLCanvasElement | undefined;
  let original: HTMLImageElement | undefined;
  let mask: HTMLImageElement | undefined;

  try {
    [original, mask] = await Promise.all([
      loadImage(originalUrl, 'Could not decode the original upload'),
      loadImage(maskUrl, 'Could not decode the mask')
    ]);

    const originalWidth = original.naturalWidth || original.width;
    const originalHeight = original.naturalHeight || original.height;
    if (originalWidth !== transform.imageWidth || originalHeight !== transform.imageHeight) {
      throw new Error(
        `Original decoded at ${originalWidth}x${originalHeight}, the editor drew ${transform.imageWidth}x${transform.imageHeight}`
      );
    }

    const maskAspect = (mask.naturalHeight || mask.height) / (mask.naturalWidth || mask.width);
    const geometry = computePrintCropGeometry({ transform, maskAspect });

    canvas = document.createElement('canvas');
    canvas.width = geometry.outputWidth;
    canvas.height = geometry.outputHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not get a 2D canvas context');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // The original, framed as the editor framed it.
    ctx.setTransform(...printCropMatrix(transform, geometry));
    ctx.drawImage(
      original,
      -transform.drawWidth / 2,
      -transform.drawHeight / 2,
      transform.drawWidth,
      transform.drawHeight
    );

    // Clip to the mask window.
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalCompositeOperation = 'destination-in';
    const rect = maskRectOnOutput(transform, geometry, maskAspect);
    ctx.drawImage(mask, rect.x, rect.y, rect.width, rect.height);

    // A JPEG has no transparency: white behind the window keeps empty areas white, not black.
    ctx.globalCompositeOperation = 'destination-over';
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, geometry.outputWidth, geometry.outputHeight);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas!.toBlob(resolve, 'image/jpeg', PRINT_CROP_JPEG_QUALITY);
    });
    if (!blob) throw new Error('Could not encode the print crop');

    const baseName = file.name.replace(/\.[^.]+$/, ''); // Remove extension
    return new File([blob], `cropped-${baseName}.jpg`, { type: 'image/jpeg' });
  } finally {
    URL.revokeObjectURL(originalUrl);
    URL.revokeObjectURL(maskUrl);
    // Empty the sources so iOS can free the decoded bitmaps sooner
    if (original) original.src = '';
    if (mask) mask.src = '';
    if (canvas) {
      // iOS releases canvas memory sooner when the canvas is shrunk
      canvas.width = 0;
      canvas.height = 0;
    }
  }
}

/** One print crop at a time, oldest first: one decoded original in memory at once (iOS Safari),
 *  and with two saves to a slot in flight the newest still lands last. */
let printCropQueue: Promise<unknown> = Promise.resolve();

/** The crop already made (or under way) for each print source object, so a repeat reuses it. */
const printCropsBySource = new WeakMap<PrintSource, Promise<File>>();

/**
 * Cuts the print file for a masked photo from the customer's original upload.
 * Rejects on any failure; never returns the original. Calls with the same print source object share
 * one crop.
 *
 * @param {PrintSource} printSource - The original file, its mask and the editor's framing.
 * @returns {Promise<File>} The cropped JPEG print file.
 */
export function cropPrintFromOriginal(printSource: PrintSource): Promise<File> {
  const existing = printCropsBySource.get(printSource);
  if (existing) return existing;

  const crop = printCropQueue.then(() => renderPrintCrop(printSource));
  printCropQueue = crop.catch(() => undefined);
  printCropsBySource.set(printSource, crop);
  crop.catch(() => {
    // Let a later call with this object try again
    if (printCropsBySource.get(printSource) === crop) printCropsBySource.delete(printSource);
  });
  return crop;
}
